import { gql } from "@apollo/client"

import { ApiFormattedResponse } from "@models/api_formatted_response"
import { mutate, query } from "@utils/graphql"

export type AskQuestionEvalMockTemplate =
  | "FULL_FLOW"
  | "RAG_PIPELINE"
  | "ACTION_PIPELINE"
  | "INTENT_PIPELINE"

export type AskQuestionEvalMsgType =
  | "TEXT_MESSAGE"
  | "COMMENT"
  | "QUESTION"
  | "PRIVATE_REPLY"

export type AskQuestionEvalMockTemplateInputs = {
  msgType?: AskQuestionEvalMsgType
}

/**
 * Mirrors AiActionType in api-v2 QpienAi/core/aiProfile.types.ts. 'negative' was removed from
 * the backend (commit 838fb2efb) — an assertion using it can never pass.
 */
export type AiActionType = "positive" | "humanSupport" | "spam"

export const AI_ACTION_VALUES: AiActionType[] = [
  "positive",
  "humanSupport",
  "spam"
]

/** humanSupport/spam return before finalize, so FINALIZE_AI_CONTENT never runs for them. */
export const ESCALATING_AI_ACTIONS: AiActionType[] = ["humanSupport", "spam"]

/**
 * `channelType` is a PlatformType (the channel the message arrived on) — NOT a MessageType.
 * Mirrors PlatformType in api-v2 Message/core/message.types.ts.
 */
export const PLATFORM_TYPES = [
  "FACEBOOK",
  "INSTAGRAM",
  "TIKTOK",
  "WHATS_APP_WEB",
  "WHATS_APP_BUSINESS",
  "META_WHATS_APP_BUSINESS",
  "LIVE_CHAT",
  "CUSTOM_CHANNEL",
  "FORM",
  "EMAIL",
  "SURVEY",
  "TELEGRAM",
  "TRENDYOL",
  "HEPSIBURADA",
  "N11",
  "AMAZON",
  "PHONE",
  "EBAY",
  "KAUFLAND",
  "PAZARAMA",
  "WALMART"
]

export type AskQuestionEvalMockAssertions = {
  expectedStatus?: string
  expectedAiAction?: string
  expectedContentIncludes?: string[]
  requiredSteps?: string[]
  forbiddenSteps?: string[]
}

export type AskQuestionEvalMockCase = {
  query: string
  assertions?: AskQuestionEvalMockAssertions
}

export type AskQuestionEvalMock = {
  _id: string
  id: string
  title: string
  cases: AskQuestionEvalMockCase[]
  channelType?: string
  createdBy?: string
  createdAt: string
  updatedAt: string
}

export type AskQuestionEvalMockPaginate = {
  docs: AskQuestionEvalMock[]
  totalDocs: number
  hasNextPage: boolean
  offset: number
  limit: number
}

export type AskQuestionEvalMockInput = {
  id?: string
  title: string
  cases: AskQuestionEvalMockCase[]
  channelType?: string
}

export type AskQuestionEvalMockTemplateInfo = {
  id: AskQuestionEvalMockTemplate
  label: string
  description: string
  requiresMsgType: boolean
  defaultRequiredSteps: string[]
}

export const FALLBACK_MOCK_TEMPLATES: AskQuestionEvalMockTemplateInfo[] = [
  {
    id: "FULL_FLOW",
    label: "Tam Akış",
    description:
      "Müşteri sorusunu uçtan uca canlı çalıştırır: intent → RAG/aksiyon → cevap. Pinecone ve gerçek tool çağrıları kullanılır.",
    requiresMsgType: false,
    defaultRequiredSteps: ["INTENT_DETECTION_CHAT", "FINALIZE_AI_CONTENT"]
  },
  {
    id: "RAG_PIPELINE",
    label: "Sadece RAG",
    description:
      "Yalnızca RAG pipeline test edilir. Pinecone'dan gerçek kaynak çekilir, cevap üretilir.",
    requiresMsgType: true,
    defaultRequiredSteps: ["ANSWER_BY_RAG_CHAT"]
  },
  {
    id: "ACTION_PIPELINE",
    label: "Sadece Aksiyon",
    description:
      "Yalnızca aksiyon (tool call) pipeline test edilir. Gerçek tool çağrısı yapılır.",
    requiresMsgType: true,
    defaultRequiredSteps: ["ACTION_DETECTION"]
  },
  {
    id: "INTENT_PIPELINE",
    label: "Sadece Intent",
    description: "Yalnızca niyet tespiti (intent detection) çalıştırılır.",
    requiresMsgType: true,
    defaultRequiredSteps: ["INTENT_DETECTION_CHAT"]
  }
]

const MOCK_FIELDS = `
  _id
  id
  title
  cases { query assertions }
  channelType
  createdBy
  createdAt
  updatedAt
`

const LIST_AI_EVAL_MOCKS = gql`
  query listAskQuestionEvalMocks($data: AskQuestionEvalMockListInput) {
    listAskQuestionEvalMocks(data: $data) {
      success
      data {
        docs { ${MOCK_FIELDS} }
        totalDocs
        hasNextPage
        offset
        limit
      }
      message
      code
    }
  }
`

const GET_AI_EVAL_MOCK = gql`
  query getAskQuestionEvalMock($id: ID!) {
    getAskQuestionEvalMock(id: $id) {
      success
      data { ${MOCK_FIELDS} }
      message
      code
    }
  }
`

const LIST_MOCK_TEMPLATES = gql`
  query listAskQuestionEvalMockTemplates {
    listAskQuestionEvalMockTemplates {
      success
      data {
        id
        label
        description
        requiresMsgType
        defaultRequiredSteps
      }
      message
      code
    }
  }
`

const CREATE_AI_EVAL_MOCK = gql`
  mutation createAskQuestionEvalMock($data: AskQuestionEvalMockInput!) {
    createAskQuestionEvalMock(data: $data) {
      success
      data { ${MOCK_FIELDS} }
      message
      code
    }
  }
`

const UPDATE_AI_EVAL_MOCK = gql`
  mutation updateAskQuestionEvalMock(
    $id: ID!
    $data: AskQuestionEvalMockInput!
  ) {
    updateAskQuestionEvalMock(id: $id, data: $data) {
      success
      data { ${MOCK_FIELDS} }
      message
      code
    }
  }
`

const DELETE_AI_EVAL_MOCK = gql`
  mutation deleteAskQuestionEvalMock($id: ID!) {
    deleteAskQuestionEvalMock(id: $id) {
      success
      data
      message
      code
    }
  }
`

export async function listAiEvalMocks(params: {
  search?: string
  template?: AskQuestionEvalMockTemplate
  offset?: number
  limit?: number
}): Promise<ApiFormattedResponse<AskQuestionEvalMockPaginate>> {
  const response = await query<{
    listAskQuestionEvalMocks?: { data: AskQuestionEvalMockPaginate }
  }>({
    query: LIST_AI_EVAL_MOCKS,
    fetchPolicy: "network-only",
    variables: { data: params }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.listAskQuestionEvalMocks?.data
  }
}

export async function getAiEvalMock(
  id: string
): Promise<ApiFormattedResponse<AskQuestionEvalMock>> {
  const response = await query<{
    getAskQuestionEvalMock?: { data: AskQuestionEvalMock }
  }>({
    query: GET_AI_EVAL_MOCK,
    fetchPolicy: "network-only",
    variables: { id }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return { isSuccess: true, data: response.data?.getAskQuestionEvalMock?.data }
}

export async function listAiEvalMockTemplates(): Promise<
  ApiFormattedResponse<AskQuestionEvalMockTemplateInfo[]>
> {
  const response = await query<{
    listAskQuestionEvalMockTemplates?: {
      data: AskQuestionEvalMockTemplateInfo[]
    }
  }>({ query: LIST_MOCK_TEMPLATES, fetchPolicy: "network-only" })

  if (!response.isSuccess) return { ...response, data: FALLBACK_MOCK_TEMPLATES }
  const remote = response.data?.listAskQuestionEvalMockTemplates?.data
  return {
    isSuccess: true,
    data: remote && remote.length ? remote : FALLBACK_MOCK_TEMPLATES
  }
}

export async function createAiEvalMock(
  data: AskQuestionEvalMockInput
): Promise<ApiFormattedResponse<AskQuestionEvalMock>> {
  const response = await mutate<{
    createAskQuestionEvalMock?: { data: AskQuestionEvalMock }
  }>({ mutation: CREATE_AI_EVAL_MOCK, variables: { data } })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.createAskQuestionEvalMock?.data
  }
}

export async function updateAiEvalMock(
  id: string,
  data: AskQuestionEvalMockInput
): Promise<ApiFormattedResponse<AskQuestionEvalMock>> {
  const response = await mutate<{
    updateAskQuestionEvalMock?: { data: AskQuestionEvalMock }
  }>({ mutation: UPDATE_AI_EVAL_MOCK, variables: { id, data } })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.updateAskQuestionEvalMock?.data
  }
}

export async function deleteAiEvalMock(
  id: string
): Promise<ApiFormattedResponse<boolean>> {
  const response = await mutate<{
    deleteAskQuestionEvalMock?: { data: boolean }
  }>({ mutation: DELETE_AI_EVAL_MOCK, variables: { id } })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.deleteAskQuestionEvalMock?.data
  }
}

const DELETE_MANY_MOCKS = gql`
  mutation deleteManyAskQuestionEvalMocks($ids: [ID!]!) {
    deleteManyAskQuestionEvalMocks(ids: $ids) {
      success
      data
      message
      code
    }
  }
`

export async function deleteManyAiEvalMocks(
  ids: string[]
): Promise<ApiFormattedResponse<number>> {
  const response = await mutate<{
    deleteManyAskQuestionEvalMocks?: { data: number }
  }>({ mutation: DELETE_MANY_MOCKS, variables: { ids } })
  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.deleteManyAskQuestionEvalMocks?.data
  }
}
